import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { CheckCircle2, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import ErrorTable from '../../../../inventory/components/bulk-stock-upload/ErrorTable';
import { usePurchaseOrderCsvUpload } from '../usePurchaseOrderCsvUpload';
import type { Product } from '../../../../inventory/types';
import type { ValidationError, PreviewRow } from '../usePurchaseOrderCsvUpload';

interface OrgSku {
  id: string;
  sku: string;
  skuCostOverride: number | null;
}

interface ValidationStepProps {
  products: Product[];
  allSkus: OrgSku[];
}

const normalizeSku = (sku: string) => sku.trim().toUpperCase();

const ValidationStep: React.FC<ValidationStepProps> = ({ products, allSkus }) => {
  const {
    parsedRows,
    validationErrors,
    blockOnErrors,
    setValidationErrors,
    setBlockOnErrors,
    setPreviewRows,
    setSkippedRows,
    setStep,
  } = usePurchaseOrderCsvUpload();

  const [hasRun, setHasRun] = useState(false);
  const [errorsOpen, setErrorsOpen] = useState(false);

  // Location-scoped product map (keyed by normalized SKU)
  const productMap = useMemo(
    () => new Map(products.map((p) => [normalizeSku(p.sku), p])),
    [products]
  );

  // Org-wide SKU map (keyed by normalized SKU)
  const allSkuMap = useMemo(
    () => new Map(allSkus.map((s) => [normalizeSku(s.sku), s])),
    [allSkus]
  );

  // Run client-side validation once on mount
  useEffect(() => {
    if (hasRun) return;
    setHasRun(true);

    const errors: ValidationError[] = [];
    const seenSkus = new Set<string>();

    for (let i = 0; i < parsedRows.length; i++) {
      const row = parsedRows[i];
      if (!row) continue;
      const csvRow = row.rowNumber + 1; // parser's rowNumber is 1-indexed in data; add 1 for header

      const rawSku = row.sku || '';
      if (!rawSku || rawSku.trim() === '') {
        errors.push({
          row: csvRow,
          sku: '',
          reason: 'SKU is empty',
          quantity: row.order_quantity,
        });
        continue;
      }

      if (row.order_quantity <= 0) {
        errors.push({
          row: csvRow,
          sku: rawSku,
          reason: 'Quantity must be greater than 0',
          quantity: row.order_quantity,
        });
        continue;
      }

      if (row.order_quantity > 999999) {
        errors.push({
          row: csvRow,
          sku: rawSku,
          reason: 'Quantity exceeds maximum (999,999)',
          quantity: row.order_quantity,
        });
        continue;
      }

      const normalized = normalizeSku(rawSku);

      // Check SKU exists org-wide (not just at this location)
      if (!allSkuMap.has(normalized)) {
        errors.push({
          row: csvRow,
          sku: rawSku,
          reason: 'SKU not found in organization',
          quantity: row.order_quantity,
        });
        continue;
      }

      // Duplicates within file — record a non-blocking warning; quantities get aggregated in preview
      if (seenSkus.has(normalized)) {
        // Intentionally not pushing an error here — duplicates are aggregated.
        // If strict duplicate detection is desired, a separate warnings surface can be added.
      }
      seenSkus.add(normalized);
    }

    setValidationErrors(errors);

    // Auto-expand if few errors
    if (errors.length > 0 && errors.length < 10) {
      setErrorsOpen(true);
    }
  }, [hasRun, parsedRows, allSkuMap, setValidationErrors]);

  const errorRowNumbers = useMemo(
    () => new Set(validationErrors.map((e) => e.row)),
    [validationErrors]
  );

  const validRowCount = parsedRows.filter(
    (r) => !errorRowNumbers.has(r.rowNumber + 1)
  ).length;

  const buildPreviewAndAdvance = () => {
    const errorRows = new Set(validationErrors.map((e) => e.row));
    const validRows = parsedRows.filter((r) => !errorRows.has(r.rowNumber + 1));
    const skipped = parsedRows.filter((r) => errorRows.has(r.rowNumber + 1));

    // Aggregate duplicates by normalized SKU
    const aggregated = new Map<string, {
      sku: string;
      quantity: number;
      rowCount: number;
    }>();

    for (const row of validRows) {
      const normalized = normalizeSku(row.sku);
      const existing = aggregated.get(normalized);
      if (existing) {
        existing.quantity += row.order_quantity;
        existing.rowCount += 1;
      } else {
        aggregated.set(normalized, {
          sku: row.sku,
          quantity: row.order_quantity,
          rowCount: 1,
        });
      }
    }

    const preview: PreviewRow[] = [];
    for (const [normalized, agg] of aggregated) {
      const product = productMap.get(normalized);
      const orgSku = allSkuMap.get(normalized);
      if (!orgSku) continue; // should not happen — we filtered errors above

      const currentStock = product?.stockLevel ?? 0;
      // Cost fallback chain: location WAC → location effective (the Default
      // cost) → org SKU Default cost. The last is critical for
      // new-at-location SKUs that have no location-level cost yet.
      const unitCost =
        product?.weightedAverageCost ??
        product?.effectiveCost ??
        orgSku.skuCostOverride ??
        null;
      const totalCostImpact = unitCost != null ? unitCost * agg.quantity : null;
      const isNewAtLocation = !product;

      preview.push({
        sku: product?.sku ?? agg.sku,
        productId: orgSku.id,
        currentStock,
        quantity: agg.quantity,
        unitCost,
        totalCostImpact,
        isNewAtLocation,
        rowCount: agg.rowCount,
      });
    }

    setPreviewRows(preview);
    setSkippedRows(skipped);
    setStep('preview');
  };

  // Auto-advance if no errors (with brief delay so user sees the success state)
  useEffect(() => {
    if (hasRun && validationErrors.length === 0 && parsedRows.length > 0) {
      const timer = setTimeout(buildPreviewAndAdvance, 600);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRun, validationErrors.length]);

  const handleContinue = () => {
    buildPreviewAndAdvance();
  };

  const handleChangeFile = () => {
    setStep('upload');
    setValidationErrors([]);
  };

  // If still running validation, show nothing extra
  if (!hasRun) return null;

  // No errors — brief green flash before auto-advance
  if (validationErrors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3">
        <CheckCircle2 className="h-10 w-10 text-green-500" />
        <p className="text-sm font-medium text-green-700">
          All {parsedRows.length} rows valid
        </p>
        <p className="text-xs text-muted-foreground">Preparing preview...</p>
      </div>
    );
  }

  // Errors present
  return (
    <div className="space-y-4">
      {/* Summary */}
      <Alert className="border-amber-200 bg-amber-50">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong>{validationErrors.length}</strong> of{' '}
          <strong>{parsedRows.length}</strong> rows have errors
        </AlertDescription>
      </Alert>

      {/* Error mode toggle */}
      <div className="flex flex-col gap-1.5 p-3 rounded-md border bg-muted/30">
        <div className="flex items-center gap-3">
          <Switch
            id="po-error-mode"
            checked={!blockOnErrors}
            onCheckedChange={(checked) => setBlockOnErrors(!checked)}
          />
          <Label htmlFor="po-error-mode" className="text-sm cursor-pointer">
            Continue with valid rows
          </Label>
        </div>
        <p className="text-xs text-muted-foreground pl-[44px]">
          {blockOnErrors
            ? 'Upload blocked: fix all errors first'
            : `${validRowCount} valid row${validRowCount !== 1 ? 's' : ''} will be imported, ${validationErrors.length} row${validationErrors.length !== 1 ? 's' : ''} will be skipped`}
        </p>
      </div>

      {/* Collapsible error details */}
      <Collapsible open={errorsOpen} onOpenChange={setErrorsOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between px-3"
          >
            <span className="text-sm">
              {errorsOpen ? 'Hide' : 'Show'} {validationErrors.length} error
              {validationErrors.length !== 1 ? 's' : ''}
            </span>
            {errorsOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <ErrorTable errors={validationErrors} />
        </CollapsibleContent>
      </Collapsible>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={handleChangeFile}>
          Change File
        </Button>
        {!blockOnErrors && validRowCount > 0 && (
          <Button
            onClick={handleContinue}
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            Continue with {validRowCount} valid rows
          </Button>
        )}
      </div>
    </div>
  );
};

export default ValidationStep;
